import React, { useMemo } from 'react';
import { Box, Text } from '@qga/roo-ui/components';
import { isEmpty, path } from 'ramda';

import Tile from '@shared/propTypes/Tile';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import FeaturedProperty from '@/components/FeaturedProperty';
import Pill from '@/components/Pill';
import PriceInPoints from '@/components/PriceInPoints';
import PricePerTraveller from '@/components/PricePerTraveller';
import { ORIGINS } from '@/constants';
import formatItemsSentence from '@/utils/formatItemsSentence';

const PropertyOption = ({ tile }) => {
  const { usePoints } = useCustomerSelection();

  const { featuredProperty } = tile.campaign;

  const price = useMemo(
    () => path(['cheapestOrigin', 'price'])(featuredProperty),
    [featuredProperty],
  );

  const origin = useMemo(() => {
    const code = path(['cheapestOrigin', 'origin', 'code'])(featuredProperty);
    return ORIGINS.find((c) => c.code === code);
  }, [featuredProperty]);

  const content = useMemo(() => {
    const otherAvailableOrigins = featuredProperty.propertyOrigins.reduce(
      (acc, cur) => [...acc, cur.name],
      [],
    );

    if (isEmpty(otherAvailableOrigins)) {
      return null;
    }

    return (
      <Box mt={6} fontSize="sm" color="greys.steel">
        Also available from
        {` ${formatItemsSentence(otherAvailableOrigins)}`}
      </Box>
    );
  }, [featuredProperty]);

  return (
    <Box position="relative">
      <FeaturedProperty
        price={
          price && (
            <Box>
              {usePoints ? (
                <PriceInPoints
                  travellers={featuredProperty.travellers}
                  total={price.total}
                />
              ) : (
                <PricePerTraveller
                  perTraveller={price.perTraveller}
                  travellers={featuredProperty.travellers}
                  total={price.total}
                />
              )}
              {origin && (
                <Text fontSize={['2xs', null, 'sm']} color="greys.steel">
                  {`Departing ${origin.name}`}
                </Text>
              )}
            </Box>
          )
        }
        nights={featuredProperty.nights}
        propertyContent={{
          ...tile,
          tagline: featuredProperty.tagline,
        }}
        content={content}
      />
      {!!featuredProperty.pillText && (
        <Pill placement="topLeft">
          {featuredProperty.pillText.toUpperCase()}
        </Pill>
      )}
    </Box>
  );
};

PropertyOption.propTypes = {
  tile: Tile.isRequired,
};

export default PropertyOption;
